import { useEffect, useRef, useState } from 'react';
import {
  activeKeyId, bytesToBase64, getCAs, isSealOwner,
  listKeys, loadEuSign, onCmpProgress,
  readPrivateKey, removeKey, resetKey, signDataInternal,
  type EuSignCA, type KeyOwnerInfo, type StoredKey,
} from './eusign';
import { kepKeys, type KepKeyMeta } from '@/api/dpsSubmissions';
import { api } from '@/api/client';

/**
 * Реєстрація АКТИВАЦІЇ ключа на сервері (0428, рішення власника
 * 2026-08-16: «простої авторизації ключа має бути достатньо» для
 * підказки ЕДО «контрагент у системі»). Браузер підписує серверний
 * challenge щойно зчитаним ключем → сервер криптоперевіряє
 * (verify-service) і фіксує код ЄДРПОУ/РНОКПП. Best-effort: помилка не
 * заважає роботі з ключем.
 */
async function registerKepActivation(): Promise<void> {
  try {
    const { data } = await api.get<{ challenge: string }>(
      '/eusign/activation-challenge');
    const sig = signDataInternal(new TextEncoder().encode(data.challenge));
    await api.post('/eusign/activation', {
      challenge: data.challenge,
      signature_b64: bytesToBase64(sig),
    });
  } catch { /* тихо: активація — лише підказка ЕДО */ }
}

/**
 * «Авторизація ключем КЕП» для сторінок з криптооперціями (Кабінет ДПС).
 *
 * Підтримує КІЛЬКА ключів одночасно (бухгалтери на кілька юросіб): кожен ключ
 * зчитується один раз (файл+пароль), далі активний усю сесію вкладки; при
 * підписанні сторінка сама обирає потрібний ключ за кодом юрособи документа.
 *
 * СХОВИЩЕ КЛЮЧІВ (0357, за ЯВНОЮ згодою — галочка при зчитуванні): файл ключа
 * зберігається на сервері (контейнер і так зашифрований паролем юзера; пароль
 * НЕ зберігається і на сервер НЕ передається — розшифровка тільки у браузері).
 * Тоді наступного разу досить ввести ПАРОЛЬ (бейдж сірий → клік → пароль →
 * зелений/активний на сесію). Без згоди — як раніше: ключ лише у пам'яті
 * вкладки, F5 очищає.
 */

interface Props {
  /** Виклик при зміні набору ключів — сторінка оновлює кнопки/лічильники. */
  onKeysChange?: (keys: StoredKey[]) => void;
}

function ownerLabel(o: KeyOwnerInfo): string {
  const code = o.subjEDRPOUCode ? `ЄДРПОУ ${o.subjEDRPOUCode}`
    : o.subjDRFOCode ? `РНОКПП ${o.subjDRFOCode}` : '';
  const who = o.subjOrg || o.subjCN;
  const signer = o.subjOrg && o.subjCN ? `; підписант ${o.subjCN}` : '';
  return `${who}${code ? ` — ${code}` : ''}${signer}`;
}

/** Коротка назва для бейджа: юрособа (без ТОВ/лапок), інакше ПІБ підписанта. */
function shortName(raw: string): string {
  const short = raw
    .replace(/^(ТОВАРИСТВО З ОБМЕЖЕНОЮ ВІДПОВІДАЛЬНІСТЮ|ТОВ|ПРИВАТНЕ ПІДПРИЄМСТВО|ПП|ФОП)\s*/iu, '')
    .replace(/["«»']/gu, '')
    .trim();
  const name = short || raw || 'ключ';
  return name.length <= 22 ? name : `${name.slice(0, 21)}…`;
}
const badgeLabel = (o: KeyOwnerInfo) => shortName((o.subjOrg || o.subjCN || '').trim());
const srvBadgeLabel = (m: KepKeyMeta) =>
  shortName((m.subj_org || m.subj_cn || '').trim());
/** Іконка типу ключа (2026-08-14, юзери з двома ключами однієї орг):
 *  🔑 — ключ ПІДПИСУ (особа), 🏷 — електронна ПЕЧАТКА (організація). */
const keyIcon = (seal: boolean) => (
  <span title={seal ? 'Електронна печатка організації' : 'Ключ підпису'}
    style={{ fontSize: '0.72rem' }}>
    {seal ? '🏷' : '🔑'}
  </span>
);
const srvIsSeal = (m: KepKeyMeta) => !!m.edrpou && !(m.drfo || '').trim();
const srvTitle = (m: KepKeyMeta) => {
  const code = m.edrpou ? `ЄДРПОУ ${m.edrpou}` : m.drfo ? `РНОКПП ${m.drfo}` : '';
  return `${m.subj_org || m.subj_cn || ''}${code ? ` — ${code}` : ''}\n`
    + `${m.file_name}\nЗбережено на сервері — клік, щоб активувати паролем`;
};

const b64ToBytes = (b64: string): Uint8Array =>
  Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

export default function KeyAuthPanel({ onKeysChange }: Props) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cas, setCas] = useState<EuSignCA[]>([]);
  const [caIdx, setCaIdx] = useState('auto');
  const [password, setPassword] = useState('');
  const [keys, setKeys] = useState<StoredKey[]>([]);
  // Згода зберегти файл ключа на сервері (0357). За замовчуванням ВИМКНЕНО.
  const [saveToServer, setSaveToServer] = useState(false);
  // Збережені на сервері ключі (метадані) + активація паролем.
  const [serverKeys, setServerKeys] = useState<KepKeyMeta[]>([]);
  const [pendingSrv, setPendingSrv] = useState<KepKeyMeta | null>(null);
  const [srvPassword, setSrvPassword] = useState('');
  const keyFileRef = useRef<{ name: string; bytes: Uint8Array } | null>(null);

  function refresh() {
    const list = [...listKeys()];
    setKeys(list);
    onKeysChange?.(list);
  }

  async function loadServerKeys() {
    try {
      setServerKeys(await kepKeys.list());
    } catch {
      // 401/503/мережа — сховище недоступне; працюємо як раніше (без нього).
      setServerKeys([]);
    }
  }

  // Ключі вже зчитані на іншій сторінці → показати одразу; сховище — фоном.
  // Інтервал — щоб протухлі за TTL ключі зникали без перезавантаження
  // (зауваження власника 2026-08-16: зелені бейджі «вічно»).
  useEffect(() => {
    if (listKeys().length) refresh();
    loadServerKeys();
    const t = setInterval(() => refresh(), 60_000);
    // Миттєве оновлення бейджів, коли реєстр змінився поза діями юзера
    // (протухання TTL, невідновний ключ прибрано при activateKey).
    const onChanged = () => refresh();
    window.addEventListener('eusign-keys-changed', onChanged);
    return () => {
      clearInterval(t);
      window.removeEventListener('eusign-keys-changed', onChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Прогрес авто-перебору АЦСК (CMP): «перевіряємо N з M» замість мертвої
  // сторінки на хвилину (скарга власника 2026-08-18/19).
  const [cmpProgress, setCmpProgress] = useState<string | null>(null);

  async function openForm() {
    setBusy(true); setError(null);
    try {
      await loadEuSign();
      setCas(await getCAs());
      setOpen(true);
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  function onKeyFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const rd = new FileReader();
    rd.onloadend = () => {
      keyFileRef.current = { name: f.name, bytes: new Uint8Array(rd.result as ArrayBuffer) };
    };
    rd.readAsArrayBuffer(f);
  }

  async function readKey() {
    if (!keyFileRef.current) { setError('Оберіть файл ключа'); return; }
    if (!password) { setError('Вкажіть пароль ключа'); return; }
    setBusy(true); setError(null);
    onCmpProgress((p) => setCmpProgress(
      `Шукаємо ваш АЦСК: ${p.done} з ${p.total} (${p.host})…`));
    try {
      const ca = caIdx === 'auto' ? null : cas[Number(caIdx)] ?? null;
      const file = keyFileRef.current;
      const owner = await readPrivateKey(file.bytes, password, ca, file.name);
      void registerKepActivation();
      // За згодою — зберегти ФАЙЛ (без пароля!) на сервері: далі досить пароля.
      if (saveToServer) {
        try {
          await kepKeys.save({
            file_name: file.name,
            key_base64: bytesToBase64(file.bytes),
            serial: owner.serial,
            subj_cn: owner.subjCN, subj_org: owner.subjOrg ?? null,
            issuer_cn: owner.issuerCN,
            edrpou: owner.subjEDRPOUCode ?? null,
            drfo: owner.subjDRFOCode ?? null,
          });
          await loadServerKeys();
        } catch (e: any) {
          // Ключ уже активний у сесії — збереження не критичне, покажемо чому.
          setError('Ключ активовано, але зберегти на сервері не вдалося: '
            + (e?.response?.data?.detail ?? e?.message ?? String(e)));
        }
      }
      setOpen(false);
      setPassword('');
      keyFileRef.current = null;
      refresh();
    } catch (e: any) {
      // Авто-перебір вичерпано → лишаємо форму відкритою, юзер обирає АЦСК
      // у селекті вище (повідомлення пояснює саме це).
      setError(e?.message ?? String(e));
    } finally {
      onCmpProgress(null);
      setCmpProgress(null);
      setBusy(false);
    }
  }

  /** Активувати збережений на сервері ключ: контейнер з сервера + пароль. */
  async function activateServerKey() {
    if (!pendingSrv || !srvPassword) return;
    setBusy(true); setError(null);
    try {
      await loadEuSign();
      const { key_base64, file_name } = await kepKeys.blob(pendingSrv.id);
      await readPrivateKey(b64ToBytes(key_base64), srvPassword, null, file_name);
      void registerKepActivation();
      setPendingSrv(null);
      setSrvPassword('');
      refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  async function dropServerKey(m: KepKeyMeta) {
    if (!window.confirm(
      `Видалити ключ «${srvBadgeLabel(m)}» зі сховища на сервері?`)) return;
    try {
      await kepKeys.remove(m.id);
      await loadServerKeys();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    }
  }

  function drop(id: string) {
    removeKey(id);
    refresh();
  }

  function dropAll() {
    resetKey();
    refresh();
  }

  const active = activeKeyId();
  // Серверні ключі, ще НЕ активовані у сесії (немає активного з тим самим serial).
  const activeSerials = new Set(keys.map((k) => k.owner.serial));
  const inactiveSrv = serverKeys.filter((m) => !activeSerials.has(m.serial));
  const totalCount = keys.length + inactiveSrv.length;

  return (
    <div style={{ marginBottom: '0.75rem' }}>
      {totalCount > 0 && (
        <div style={{ background: 'var(--badge-green-bg)', border: '1px solid var(--badge-green-bd)',
          borderRadius: 6, padding: '0.45rem 0.6rem', marginBottom: '0.5rem',
          fontSize: '0.8rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem',
            flexWrap: 'wrap' }}>
            <b style={{ marginRight: '0.2rem' }}>Ключі КЕП ({totalCount})</b>
            {/* Зелений бейдж — ключ АКТИВНИЙ у цій сесії (можна підписувати). */}
            {keys.map((k) => (
              <span key={k.id}
                title={`${ownerLabel(k.owner)}\n${k.fileName}\nАктивний у цій сесії`
                  + (active === k.id ? ' (поточний)' : '')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: 'var(--bg-panel)', border: '1px solid var(--badge-green-bd)',
                  borderRadius: 999, padding: '0.1rem 0.2rem 0.1rem 0.5rem',
                  maxWidth: 190, fontSize: '0.76rem', lineHeight: 1.5 }}>
                {keyIcon(isSealOwner(k.owner))}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap' }}>
                  {badgeLabel(k.owner)}
                </span>
                <button style={{ padding: '0 0.3rem', fontSize: '0.85rem',
                  color: '#dc2626', lineHeight: 1, border: 'none',
                  background: 'transparent', cursor: 'pointer' }}
                  title="Прибрати з сесії (якщо збережений на сервері — лишиться там)"
                  onClick={() => drop(k.id)}>×</button>
              </span>
            ))}
            {/* Сірий бейдж — збережений на сервері, НЕ активований: клік → пароль. */}
            {inactiveSrv.map((m) => (
              <span key={m.id} title={srvTitle(m)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: 'var(--bg-soft-2)', border: '1px dashed var(--border-strong)',
                  color: 'var(--text-secondary)', borderRadius: 999,
                  padding: '0.1rem 0.2rem 0.1rem 0.5rem',
                  maxWidth: 190, fontSize: '0.76rem', lineHeight: 1.5 }}>
                {keyIcon(srvIsSeal(m))}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap', cursor: 'pointer' }}
                  onClick={() => { setPendingSrv(m); setSrvPassword(''); setError(null); }}>
                  {srvBadgeLabel(m)}
                </span>
                <button style={{ padding: '0 0.3rem', fontSize: '0.85rem',
                  color: '#94a3b8', lineHeight: 1, border: 'none',
                  background: 'transparent', cursor: 'pointer' }}
                  title="Видалити зі сховища на сервері"
                  onClick={() => dropServerKey(m)}>×</button>
              </span>
            ))}
            {keys.length > 0 && (
              <button style={{ marginLeft: 'auto', padding: '0.15rem 0.5rem',
                fontSize: '0.75rem' }}
                title="Прибрати всі активні ключі з сесії (збережені на сервері лишаться)"
                onClick={dropAll}>очистити сесію</button>
            )}
          </div>
          {/* Активація збереженого ключа: лише пароль. */}
          {pendingSrv && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
              marginTop: '0.45rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.78rem' }}>
                Пароль ключа «{srvBadgeLabel(pendingSrv)}»:
              </span>
              <input type="password" autoFocus value={srvPassword}
                onChange={(e) => setSrvPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') activateServerKey(); }}
                style={{ width: 160 }} />
              <button className="primary" disabled={busy || !srvPassword}
                style={{ padding: '0.2rem 0.6rem', fontSize: '0.78rem' }}
                onClick={activateServerKey}>
                {busy ? 'активація…' : 'активувати'}
              </button>
              <button style={{ padding: '0.2rem 0.6rem', fontSize: '0.78rem' }}
                onClick={() => { setPendingSrv(null); setSrvPassword(''); }}>
                скасувати
              </button>
            </div>
          )}
        </div>
      )}

      {!open ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={openForm} disabled={busy}>
            {busy ? 'завантаження…'
              : totalCount ? '+ додати ще ключ КЕП' : 'зчитати ключ КЕП'}
          </button>
          <span style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>
            {totalCount
              ? 'сірий бейдж — збережений ключ: клік → пароль → активний на сесію'
              : 'читається у браузері; пароль на сервер не передається'}
          </span>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.75rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="field" style={{ minWidth: 260 }}>
              <label>Надавач послуг (АЦСК) — необов'язково</label>
              <select value={caIdx} onChange={(e) => setCaIdx(e.target.value)}>
                <option value="auto">визначити автоматично</option>
                {cas.map((c, i) => <option key={i} value={i}>{c.issuerCNs[0]}</option>)}
              </select>
            </div>
            <div className="field" style={{ minWidth: 230 }}>
              <label>Файл ключа (Key-6.dat / *.pfx / *.zs2)</label>
              <input type="file" onChange={onKeyFile} accept=".dat,.pfx,.pk8,.zs2,.jks,.p12" />
            </div>
            <div className="field" style={{ width: 160 }}>
              <label>Пароль ключа</label>
              <input type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} />
            </div>
          </div>
          {/* Вибір зберігання ФАЙЛА ключа (пароль НЕ зберігається у будь-якому
              разі). За замовчуванням — НЕ зберігати. */}
          <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap',
            marginTop: '0.7rem', fontSize: '0.8rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6,
              cursor: 'pointer' }}>
              <input type="radio" name="kep-store" checked={!saveToServer}
                onChange={() => setSaveToServer(false)} />
              Не зберігати ключ на сервері
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6,
              cursor: 'pointer' }}>
              <input type="radio" name="kep-store" checked={saveToServer}
                onChange={() => setSaveToServer(true)} />
              Зберегти ключ на сервері (наступного разу досить ввести лише пароль)
            </label>
          </div>
          {/* Кнопки — окремим нижнім рядком. */}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.7rem' }}>
            <button className="primary" onClick={readKey} disabled={busy}>
              {busy ? 'зчитування…' : 'зчитати ключ'}
            </button>
            <button onClick={() => { setOpen(false); setPassword(''); }}>скасувати</button>
          </div>
        </div>
      )}
      {cmpProgress && (
        <div style={{ color: '#0369a1', fontSize: '0.8rem', marginTop: 4 }}>
          {cmpProgress}
        </div>
      )}
      {error && <div style={{ color: '#b00', fontSize: '0.8rem', marginTop: 4 }}>{error}</div>}
    </div>
  );
}
